import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "urql";
import { useParams, useLocation, Link } from "wouter";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Alert, AlertVariant } from "../../../components/src/Alert";
import { cloneDeep, keyBy } from "lodash";

import { YesNoModal } from "../../../components/src/YesNoModal";
import { TextField, SelectField, SearchListField, SubmitButton, NumberField, DateField, CheckBoxField } from "../../../components/src/form";
import { addWeekDays, generateRandomString, useAlertTimeOut } from "../../../components/src/common/Utils";
import { usePrevious } from "../../../components/src/common/Utils";

import { serializeSwedenDate } from "../../../components/src/dateFormater";
import {
    PartyFundInfoClass,
    TransferAgentOrder,
    TransferAgentOrderInput,
    CurrencyEnum,
    TransferAgentOrderEnum,
    TransactionStatus,
    TransferAgentOrderSubTypeEnum
} from "../types.generated";
import { MarkDownField } from "../components/form/MarkDownField";
import { AttachmentForm } from "./AttachmentForm";
import { recursivelyRemoveKey } from "../../../common/src";
import { REACT_APP_API_URI } from "../env";
import {
    GET_TRANSFER_AGENT_ORDER_DATA,
    GET_TRANSFER_AGENT_ORDER_LAST_NAV,
    UPDATE_TRANSFER_AGENT_ORDER_ATTACHMENTS_FORM,
    UPSERT_TRANSFER_AGENT_ORDERS_FORM
} from "../../../graphql/src/transferagent/queries";

type TransferAgentOrderForm = Partial<
    Omit<TransferAgentOrderInput, "amount" | "units"> & {
        amount: string;
        units: number;
        instrumentId?: string;
        toClientNumber?: string;
        toClientAccount?: string;
        toClientName?: string;
    }
>;

type TransferAgentOrderFormProps = {
    refetch?: () => void;
};

export function TransferAgentOrderForm(props: TransferAgentOrderFormProps): React.ReactElement {
    const { id }: any = useParams();
    const previousId = usePrevious(id);

    let orderId = "000000000000000000000000";
    if (id && id !== "new") {
        orderId = id;
    }

    const [{ fetching: loading, error, data }, refetch] = useQuery({ query: GET_TRANSFER_AGENT_ORDER_DATA, variables: { orderId } });

    const [modal, setModal] = useState({ showModal: false, payload: null });
    const [formData, setFormData] = useState<TransferAgentOrderForm>(null);

    const [orderAttachments, setOrderAttachments] = useState([]);
    const [orderClientId, setOrderClientId] = useState(null);

    const [__stateUpsert, upsertMutation] = useMutation(UPSERT_TRANSFER_AGENT_ORDERS_FORM);
    const [__stateUpsertAttachments, upsertAttachmentsMutation] = useMutation(UPDATE_TRANSFER_AGENT_ORDER_ATTACHMENTS_FORM);

    const [location, navigate] = useLocation();

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    let selectedFundId = null;
    if (data && data.funds && formData && formData.instrumentId) {
        const matchedFund = data.funds.find((fund) => {
            if (!fund.fundInfo || !fund.fundInfo.classes) {
                return false;
            }
            return fund.fundInfo.classes.some((c) => c.instrument && c.instrument._id === formData.instrumentId);
        });
        if (matchedFund) {
            selectedFundId = matchedFund._id;
        }
    }
    if (!selectedFundId && data && data.order && data.order.clientId) {
        selectedFundId = data.order.clientId;
    }

    const [{ data: lastNavData }] = useQuery({
        query: GET_TRANSFER_AGENT_ORDER_LAST_NAV,
        variables: { clientId: selectedFundId },
        pause: !selectedFundId
    });

    const isins = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const instruments = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fundClassByInstrumentId: Record<string, PartyFundInfoClass> = {};
    if (data && data.funds) {
        data.funds.forEach((fund) => {
            fund.fundInfo.classes.forEach((c: PartyFundInfoClass) => {
                //if (c.enabled) { Cannot only have enabled classes, cause then you cannot see old orders
                isins.push(c.instrument.isin);
                instruments.push(c.instrument);
                fundClassByInstrumentId[c.instrument._id] = c;
            });
        });
    }

    const instrumentsByIsin = keyBy(instruments, "isin");
    const instrumentsById = keyBy(instruments, "_id");

    useEffect(() => {
        if (previousId && previousId !== id) {
            setFormData(null);
        } else if (!loading && !error) {
            if (formData === null) {
                if (id && id !== "new" && data.order) {
                    const order: TransferAgentOrder = data.order;
                    const instrument = instrumentsByIsin[order.isin];
                    const initFormData: TransferAgentOrderForm = {
                        _id: order._id,
                        carryOwnTransactionCost: order.carryOwnTransactionCost,
                        currency: order.currency,
                        amount: parseFloat(order.amount).toFixed(2),
                        units: parseFloat(order.units),
                        instrumentId: instrument._id,
                        tradeDate: order.tradeDate,
                        valueDate: order.valueDate,
                        transactionType: order.transactionType,
                        externalId: order.externalId,
                        sourceId: order.sourceId,
                        clientName: order.clientName,
                        clientNumber: order.clientNumber,
                        clientAccount: order.clientAccount,
                        status: order.status,
                        transactionIds: order.transactionIds,
                        description: order.description || "",
                        subType: order.subType,
                        referenceExternalId: order.referenceExternalId
                    };
                    setFormData(initFormData);
                    setOrderClientId(data.order.clientId);
                    setOrderAttachments(cloneDeep(data.order.attachments));
                } else {
                    // default
                    const today = serializeSwedenDate(new Date());
                    const instrumentId = instruments[0]._id;
                    const initFormData: TransferAgentOrderForm = {
                        _id: "new",
                        carryOwnTransactionCost: fundClassByInstrumentId[instrumentId].shareClass === "C" ? true : false,
                        currency: CurrencyEnum.SEK,
                        amount: "0.00",
                        units: 0.0,
                        instrumentId: instrumentId,
                        tradeDate: today,
                        valueDate: addWeekDays(today, 2),
                        transactionType: TransferAgentOrderEnum.BUY,
                        externalId: "",
                        sourceId: data.transferAgents[0]?._id ?? "",
                        clientName: "",
                        clientNumber: "",
                        clientAccount: "",
                        status: TransactionStatus.Pending,
                        transactionIds: [],
                        description: "",
                        subType: TransferAgentOrderSubTypeEnum.NORMAL,
                        referenceExternalId: ""
                    };

                    setFormData(initFormData);
                }
            }
        }
    }, [previousId, id, loading, error, formData, data, instrumentsByIsin, instruments, fundClassByInstrumentId]);

    const onChangeAttachment = useCallback(
        (attachments) => {
            const cleanAttachments = recursivelyRemoveKey(attachments, "__typename");

            const input = {
                _id: orderId,
                attachments: cleanAttachments
            };

            upsertAttachmentsMutation({ input }).then(() => {
                setOrderAttachments(cleanAttachments);
            });
        },
        [orderId, upsertAttachmentsMutation, setOrderAttachments]
    );

    if (error)
        return (
            <div>
                <h3>Failed loading data</h3>
            </div>
        );

    if (loading)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    if (orderId !== "000000000000000000000000" && !data.order)
        return (
            <div>
                <h3>No order with that id</h3>
            </div>
        );

    if (!formData) return <div></div>;

    let isCreateMode = false;
    if (id === "new") {
        isCreateMode = true;
    }

    const pageTitle = isCreateMode ? "New order" : "Edit order";
    const submitButtonLabel = isCreateMode ? "Create" : "Update";

    const transferAgentOptions = data.transferAgents.map((d) => ({ key: d._id, value: d.name }));
    transferAgentOptions.unshift({ key: "000000000000000000000000", value: "None" });
    let minDate = new Date("2001-01-01");
    if (lastNavData && lastNavData.navs && lastNavData.navs.length > 0 && lastNavData.navs[0].date) {
        minDate = new Date(lastNavData.navs[0].date);
    }
    minDate.setDate(minDate.getDate() + 1);

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete order with id " + modal.payload.input._id + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        let mutationData: any;
                        upsertMutation({ input: [modal.payload.input] })
                            .then((result) => {
                                if (result.error) {
                                    setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                } else {
                                    mutationData = result.data.upsert[0];
                                    setAlert({
                                        color: "success",
                                        visible: true,
                                        message: `The order '${modal.payload.input._id}' updated and deleted successfully!`
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error.toString());
                            })
                            .finally(() => {
                                refetch();
                                if (mutationData && mutationData._id) {
                                    mutationData.instrumentId =
                                        instrumentsByIsin && instrumentsByIsin[mutationData.isin]
                                            ? instrumentsByIsin[mutationData.isin]._id
                                            : null;
                                    mutationData.amount = parseFloat(mutationData.amount).toFixed(2);
                                    setFormData(mutationData);
                                }
                                if (props.refetch) {
                                    props.refetch();
                                }
                            });
                    }}
                />
            ) : null}

            <h3 className="px-2">{pageTitle}</h3>

            {formData._id !== "new" && formData.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? (
                <div>
                    <div>WARNING: if you edit this order, you must also edit the corresponding order with externalId: </div>
                    <Link to={"/transferagentorders/?externalId=" + formData.referenceExternalId + "&tradeDate=" + formData.tradeDate}>
                        {formData.referenceExternalId}
                    </Link>
                </div>
            ) : null}

            <div className="px-2">
                <Formik
                    enableReinitialize={true}
                    validateOnMount={true}
                    initialValues={formData}
                    validationSchema={Yup.object({
                        externalId: Yup.string()
                            .min(5, "Must be at least 5 characters")
                            .max(30, "Must be 30 characters or less")
                            .required("Required"),
                        amount: Yup.string()
                            .matches(/^\d+\.\d{2}$/, "Amount must be a non-negative number with exactly 2 decimal places")
                            .test("min", "Amount must not be negative", (value) => !value || parseFloat(value) >= 0),
                        units: Yup.number().min(0),
                        clientAccount: Yup.string().trim().required("Required"),
                        toClientAccount: Yup.string().when(["subType", "_id"], {
                            is: (subType: string, _id: string) => subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH && _id === "new",
                            then: (schema) => schema.trim().required("Required"),
                            otherwise: (schema) => schema
                        })
                    })}
                    validate={(validateFormData: TransferAgentOrderForm) => {
                        const errors: any = {};
                        if (
                            validateFormData.subType === TransferAgentOrderSubTypeEnum.NORMAL &&
                            validateFormData.transactionType === TransferAgentOrderEnum.BUY &&
                            validateFormData.units > 0
                        ) {
                            errors.type = "BUY order of units is not supported";
                            errors.units = "BUY order of units is not supported";
                        }
                        const amountValue = parseFloat(validateFormData.amount || "0");
                        if (amountValue === 0.0 && validateFormData.units === 0.0) {
                            errors.amount = "Amount or units must be 0, not both";
                            errors.units = "Amount or units must be 0, not both";
                        }
                        if (amountValue !== 0.0 && validateFormData.units !== 0.0) {
                            errors.amount = "Amount or units must be !==0, not both";
                            errors.units = "Amount or units must be !==0, not both";
                        }

                        if (validateFormData.externalId === "") {
                            validateFormData.externalId = generateRandomString(15);
                        }

                        const fundClass = fundClassByInstrumentId[validateFormData.instrumentId];

                        if (validateFormData.instrumentId !== formData.instrumentId) {
                            if (fundClass && fundClass.shareClass === "C") {
                                validateFormData.carryOwnTransactionCost = true;
                            } else {
                                validateFormData.carryOwnTransactionCost = false;
                            }
                        }

                        if (fundClass && fundClass.instrument && fundClass.instrument.currency !== validateFormData.currency) {
                            errors.currency = `Currency must be '${fundClass.instrument.currency}'`;
                        }

                        setFormData(validateFormData);

                        console.log({ errors });

                        return Object.keys(errors).length > 0 ? errors : {};
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                        const submitValues: TransferAgentOrderForm = values;
                        let input: TransferAgentOrderInput[] = [];

                        if (submitValues._id === "new" && submitValues.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH) {
                            const externalIdSell = "SWITCH_A_" + generateRandomString(12);
                            const externalIdBuy = "SWITCH_A_" + generateRandomString(12);
                            input = [
                                {
                                    carryOwnTransactionCost: false,
                                    currency: instrumentsById[submitValues.instrumentId].currency,
                                    amount: "0.00",
                                    units: submitValues.units.toFixed(4), // TODO: decimals depending on fund
                                    isin: instrumentsById[submitValues.instrumentId].isin,
                                    tradeDate: submitValues.tradeDate,
                                    valueDate: submitValues.tradeDate,
                                    transactionType: TransferAgentOrderEnum.SELL,
                                    externalId: externalIdSell,
                                    sourceId: submitValues.sourceId,
                                    clientName: "",
                                    clientNumber: submitValues.clientNumber,
                                    clientAccount: submitValues.clientAccount,
                                    status: submitValues.status,
                                    transactionIds: submitValues.transactionIds,
                                    subType: submitValues.subType,
                                    referenceExternalId: externalIdBuy
                                },
                                {
                                    carryOwnTransactionCost: false,
                                    currency: instrumentsById[submitValues.instrumentId].currency,
                                    amount: "0.00",
                                    units: submitValues.units.toFixed(4), // TODO: decimals depending on fund
                                    isin: instrumentsById[submitValues.instrumentId].isin,
                                    tradeDate: submitValues.tradeDate,
                                    valueDate: submitValues.tradeDate,
                                    transactionType: TransferAgentOrderEnum.BUY,
                                    externalId: externalIdBuy,
                                    sourceId: submitValues.sourceId,
                                    clientName: "",
                                    clientNumber: submitValues.toClientNumber,
                                    clientAccount: submitValues.toClientAccount,
                                    status: submitValues.status,
                                    transactionIds: submitValues.transactionIds,
                                    subType: submitValues.subType,
                                    referenceExternalId: externalIdSell
                                }
                            ];
                        } else {
                            input = [
                                {
                                    _id: submitValues._id,
                                    carryOwnTransactionCost: submitValues.carryOwnTransactionCost,
                                    currency: submitValues.currency,
                                    amount: submitValues.amount,
                                    units: submitValues.units.toFixed(4), // TODO: decimals depending on fund
                                    isin: instrumentsById[submitValues.instrumentId].isin,
                                    tradeDate: submitValues.tradeDate,
                                    valueDate: submitValues.valueDate,
                                    transactionType: submitValues.transactionType,
                                    externalId: submitValues.externalId,
                                    sourceId: submitValues.sourceId,
                                    clientName: "",
                                    clientNumber: submitValues.clientNumber,
                                    clientAccount: submitValues.clientAccount,
                                    status: submitValues.status,
                                    transactionIds: submitValues.transactionIds,
                                    subType: submitValues.subType,
                                    referenceExternalId: submitValues.referenceExternalId
                                }
                            ];
                            if (isCreateMode) {
                                delete input[0]._id;
                            }
                        }

                        let mutationData: TransferAgentOrderForm = null;

                        if (!isCreateMode && input[0].status && input[0].status === TransactionStatus.Deleted) {
                            const inputDelete: any = cloneDeep(input[0]);
                            setModal({ showModal: true, payload: { input: inputDelete } });
                        } else if (!input[0].status || input[0].status !== TransactionStatus.Deleted) {
                            await upsertMutation({ input: input })
                                .then((result) => {
                                    if (result.error) {
                                        setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                    } else {
                                        mutationData = result.data.upsert[0];
                                        mutationData.instrumentId =
                                            instrumentsByIsin && instrumentsByIsin[mutationData.isin]
                                                ? instrumentsByIsin[mutationData.isin]._id
                                                : null;
                                        mutationData.amount = parseFloat(mutationData.amount).toFixed(2);
                                    }
                                })
                                .catch((error) => {
                                    console.error(error.toString());
                                })
                                .finally(() => {
                                    setSubmitting(false);
                                    // redirect to edit Order page on create success
                                    if (mutationData && mutationData._id) {
                                        if (isCreateMode) {
                                            const pathname = location.split("?")[0].split("#")[0];
                                            const path = pathname.split("/");
                                            path.pop();
                                            path.push(mutationData._id);
                                            navigate(path.join("/"), { replace: true });
                                        }
                                        refetch();
                                        setFormData(mutationData);
                                        /*if (props.refetch) {
                                            props.refetch();
                                        }*/

                                        if (input.length === 2) {
                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `The orders '${input[0].externalId}' and '${input[1].externalId}' created successfully!`
                                            });
                                        } else {
                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `The order '${input[0].externalId}' created/updated successfully!`
                                            });
                                        }
                                    }
                                });
                        }
                    }}
                >
                    {({ isSubmitting, values: valuesUntyped }) => {
                        const values: TransferAgentOrderForm = valuesUntyped;
                        return (
                            <Fragment>
                                <Form autoComplete="off">
                                    <div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full px-2">
                                                <TextField className="" name="_id" label="Id" disabled={true} />
                                            </div>
                                            {values._id === "new" &&
                                            values.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? null : (
                                                <div className="w-full px-2">
                                                    <SelectField
                                                        className=""
                                                        name="transactionType"
                                                        label="Type*"
                                                        options={Object.values(TransferAgentOrderEnum)}
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                            )}
                                            <div className="w-full px-2">
                                                <SelectField
                                                    className=""
                                                    name="subType"
                                                    label="SubType"
                                                    options={Object.values(TransferAgentOrderSubTypeEnum)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full px-2">
                                                <NumberField className="" name="units" label="Units*" disabled={isSubmitting} />
                                            </div>
                                            {values._id === "new" &&
                                            values.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? null : (
                                                <Fragment>
                                                    <div className="w-full px-2">
                                                        <NumberField
                                                            className=""
                                                            name="amount"
                                                            label="Amount*"
                                                            disabled={isSubmitting}
                                                            decimalScale={2}
                                                            fixedDecimalScale={true}
                                                            allowNegative={false}
                                                        />
                                                    </div>
                                                </Fragment>
                                            )}
                                            <div className="w-full px-2">
                                                <SelectField
                                                    className=""
                                                    name="currency"
                                                    label="Currency*"
                                                    options={["SEK", "NOK", "DKK", "EUR", "USD"]}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full px-2">
                                                <SearchListField
                                                    className=""
                                                    name="instrumentId"
                                                    label="Instrument"
                                                    options={instruments}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div className="w-full px-2">
                                                <DateField
                                                    className=""
                                                    name="tradeDate"
                                                    label="Trade date"
                                                    minDate={minDate.toDateString()}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            {values._id === "new" &&
                                            values.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? null : (
                                                <div className="w-full px-2">
                                                    <DateField
                                                        className=""
                                                        name="valueDate"
                                                        label="Value date"
                                                        minDate={minDate.toDateString()}
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {values._id === "new" && values.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? (
                                            <Fragment>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2">
                                                        <TextField
                                                            className=""
                                                            name="clientNumber"
                                                            label="From client number"
                                                            type="text"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full px-2">
                                                        <TextField
                                                            className=""
                                                            name="clientAccount"
                                                            label="From client account*"
                                                            type="text"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2">
                                                        <TextField
                                                            className=""
                                                            name="toClientNumber"
                                                            label="To client number"
                                                            type="text"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full px-2">
                                                        <TextField
                                                            className=""
                                                            name="toClientAccount"
                                                            label="To client account*"
                                                            type="text"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                            </Fragment>
                                        ) : (
                                            <div className="flex flex-wrap -mx-2">
                                                <div className="w-full px-2">
                                                    <TextField
                                                        className=""
                                                        name="clientNumber"
                                                        label="Client number"
                                                        type="text"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="w-full px-2">
                                                    <TextField
                                                        className=""
                                                        name="clientAccount"
                                                        label="Client account*"
                                                        type="text"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full px-2">
                                                <SelectField
                                                    className=""
                                                    name="status"
                                                    label="Status"
                                                    options={Object.values(TransactionStatus)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            {values._id === "new" &&
                                            values.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? null : (
                                                <Fragment>
                                                    <div className="w-full px-2">
                                                        <CheckBoxField
                                                            className=""
                                                            name="carryOwnTransactionCost"
                                                            label="Carry own transaction cost"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full px-2">
                                                        <TextField
                                                            className=""
                                                            name="externalId"
                                                            label="External id"
                                                            type="text"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </Fragment>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full px-2">
                                                <MarkDownField
                                                    name="description"
                                                    label="Description"
                                                    className=""
                                                    graphqlApiBaseUrl={REACT_APP_API_URI}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full px-2">
                                                <SelectField
                                                    className=""
                                                    name="sourceId"
                                                    label="Transfer agent"
                                                    options={transferAgentOptions}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            {values._id === "new" &&
                                            values.subType === TransferAgentOrderSubTypeEnum.ACCOUNT_SWITCH ? null : (
                                                <div className="w-full px-2">
                                                    <TextField
                                                        className=""
                                                        name="referenceExternalId"
                                                        label="Reference External Id"
                                                        type="text"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <SubmitButton disabled={isSubmitting} label={submitButtonLabel} className="btn btn-primary" />
                                    </div>
                                    {alert.visible ? (
                                        <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                                            {alert.message}
                                        </Alert>
                                    ) : null}
                                </Form>
                            </Fragment>
                        );
                    }}
                </Formik>

                {!isCreateMode ? (
                    <AttachmentForm
                        key={orderAttachments.length}
                        clientId={orderClientId}
                        attachments={orderAttachments}
                        onChange={onChangeAttachment}
                    />
                ) : null}
            </div>
        </div>
    );
}
